/**
 * Skill / Tool / 能力绑定。
 *
 * 三张表回答三个不同的问题：
 *   Skill      用户能触发什么，花多少灵力，哪个形态能用
 *   Tool       内部执行单元，用户看不到，模型也不直接调用
 *   能力绑定    某个 capability key 当前解析到哪个模型 —— 来自 packages/config
 *              而不是数据库，因为「业务代码不得内联模型 ID」
 */

import { Alert, Card, Space, Table, Tabs, Tag, Typography } from "antd";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../services/api";
import { DataTable } from "../../components/DataTable";
import { fmtCents, RelativeTime, StatusTag, Tags } from "../../components/format";

export function SkillsPage() {
  const [tab, setTab] = useState("skills");
  return (
    <Tabs
      activeKey={tab}
      onChange={setTab}
      items={[
        { key: "skills", label: "Skill", children: <SkillList /> },
        { key: "tools", label: "Tool", children: <ToolList /> },
        { key: "capabilities", label: "能力 → 模型绑定", children: <CapabilityList /> },
        { key: "executions", label: "执行记录", children: <ExecutionList /> },
        { key: "sources", label: "外部来源", children: <SourceList /> },
      ]}
    />
  );
}

interface SkillRow {
  key: string;
  displayName: string;
  description: string;
  tier: string;
  allowedTriggers: string[];
  allowedForms: string[];
  producesUntrustedContent: boolean;
  riskLevel: string;
  capabilityKey: string | null;
  binding: { kind: string; provider: string; model: string | null; status: string; note?: string } | null;
  toolKeys: string[];
  powerCost: number | null;
  status: string;
  executions: { total: number };
}

function SkillList() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["skills"],
    queryFn: () => api.get<SkillRow[]>("/api/v1/admin/skills"),
  });
  if (error) return <Alert type="error" showIcon message={(error as Error).message} />;

  return (
    <Card styles={{ body: { padding: 12 } }}>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        `SkillDefinition` **不存 powerCost**——经济数值的单一真值在 PricingPolicy，存两份会分叉。
        这一列是运行时从当前生效的策略 payload 里读出来的。
        加一个 Skill 必须改代码；运营只能改 status，不能创建。
      </Typography.Paragraph>
      <Table<SkillRow>
        size="small"
        rowKey="key"
        loading={isLoading}
        pagination={false}
        dataSource={data ?? []}
        expandable={{
          expandedRowRender: (r) => (
            <Typography.Paragraph style={{ marginBottom: 0 }}>{r.description}</Typography.Paragraph>
          ),
        }}
        columns={[
          { title: "key", dataIndex: "key", width: 180, render: (v: string) => <Typography.Text code>{v}</Typography.Text> },
          { title: "名称", dataIndex: "displayName", width: 130 },
          {
            title: "分级",
            dataIndex: "tier",
            width: 100,
            render: (v: string) => (
              <Tag color={v === "guarded" ? "red" : v === "metered" ? "orange" : "default"}>{v}</Tag>
            ),
          },
          {
            title: "灵力",
            dataIndex: "powerCost",
            width: 70,
            align: "right",
            render: (v: number | null) => (v === null ? "—" : v),
          },
          {
            title: "允许形态",
            dataIndex: "allowedForms",
            width: 160,
            render: (v: string[]) => <Tags values={v} />,
          },
          {
            title: "触发源",
            dataIndex: "allowedTriggers",
            width: 200,
            render: (v: string[]) => <Tags values={v} />,
          },
          {
            title: "能力 key",
            dataIndex: "capabilityKey",
            width: 180,
            render: (v: string | null) => (v ? <Typography.Text code>{v}</Typography.Text> : "—"),
          },
          {
            title: "解析模型",
            dataIndex: "binding",
            width: 180,
            render: (v: SkillRow["binding"]) =>
              v ? (
                <Space direction="vertical" size={0}>
                  <span>{v.model ?? v.kind}</span>
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    {v.provider} · {v.status}
                  </Typography.Text>
                </Space>
              ) : (
                "—"
              ),
          },
          {
            title: "不可信产出",
            dataIndex: "producesUntrustedContent",
            width: 100,
            render: (v: boolean) => (v ? <Tag color="orange">是</Tag> : "—"),
          },
          { title: "风险", dataIndex: "riskLevel", width: 80 },
          {
            title: "执行",
            dataIndex: "executions",
            width: 80,
            align: "right",
            render: (v: { total: number }) => v.total,
          },
          { title: "状态", dataIndex: "status", width: 100, render: (v) => <StatusTag value={v} /> },
        ]}
      />
    </Card>
  );
}

interface ToolRow {
  key: string;
  displayName: string;
  description: string;
  producesUntrustedContent: boolean;
  requiresNetwork: boolean;
  capabilityKey: string | null;
  status: string;
  executions: { total: number };
}

function ToolList() {
  const { data, isLoading } = useQuery({
    queryKey: ["tools"],
    queryFn: () => api.get<ToolRow[]>("/api/v1/admin/skills/tools"),
  });
  return (
    <Card styles={{ body: { padding: 12 } }}>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        Tool 是内部执行单元，用户看不到，模型也不直接调用（模型调 Skill）。
        `requiresNetwork` 为 true 的工具要走 SSRF 防护：限制协议、DNS/IP 段、重定向次数、响应大小、超时和 MIME。
      </Typography.Paragraph>
      <Table<ToolRow>
        size="small"
        rowKey="key"
        loading={isLoading}
        pagination={false}
        dataSource={data ?? []}
        expandable={{
          expandedRowRender: (r) => (
            <Typography.Paragraph style={{ marginBottom: 0 }}>{r.description}</Typography.Paragraph>
          ),
        }}
        columns={[
          { title: "key", dataIndex: "key", width: 220, render: (v: string) => <Typography.Text code>{v}</Typography.Text> },
          { title: "名称", dataIndex: "displayName", width: 160 },
          {
            title: "需要出网",
            dataIndex: "requiresNetwork",
            width: 100,
            render: (v: boolean) => (v ? <Tag color="orange">是</Tag> : "—"),
          },
          {
            title: "不可信产出",
            dataIndex: "producesUntrustedContent",
            width: 110,
            render: (v: boolean) => (v ? <Tag color="orange">是</Tag> : "—"),
          },
          {
            title: "能力 key",
            dataIndex: "capabilityKey",
            width: 200,
            render: (v: string | null) => (v ? <Typography.Text code>{v}</Typography.Text> : "—"),
          },
          {
            title: "执行",
            dataIndex: "executions",
            width: 80,
            align: "right",
            render: (v: { total: number }) => v?.total ?? 0,
          },
          { title: "状态", dataIndex: "status", width: 100, render: (v) => <StatusTag value={v} /> },
        ]}
      />
    </Card>
  );
}

function CapabilityList() {
  const { data, isLoading } = useQuery({
    queryKey: ["capabilities"],
    queryFn: () => api.get<Record<string, unknown>[]>("/api/v1/admin/skills/capabilities"),
  });
  return (
    <Card styles={{ body: { padding: 12 } }}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="这张表来自 packages/config/src/models.ts，不是数据库"
        description="模型绑定的单一真值在配置里。业务代码只引用 capability key，绝不内联模型 ID——chat.normal 与 chat.weakened 绑定的模型单价差 12 倍，可声明即可白嫖。"
      />
      <Table
        size="small"
        rowKey="capability"
        loading={isLoading}
        pagination={false}
        dataSource={data ?? []}
        columns={[
          {
            title: "capability",
            dataIndex: "capability",
            width: 220,
            render: (v: string) => <Typography.Text code>{v}</Typography.Text>,
          },
          { title: "类型", dataIndex: "kind", width: 140 },
          { title: "供应商", dataIndex: "provider", width: 150 },
          {
            title: "模型",
            dataIndex: "model",
            width: 200,
            render: (v: string | null) => v ?? <Typography.Text type="secondary">（无模型）</Typography.Text>,
          },
          {
            title: "绑定状态",
            dataIndex: "status",
            width: 170,
            render: (v: string) => (
              <Tag color={v === "locked" ? "green" : v === "pending_evaluation" ? "orange" : "default"}>{v}</Tag>
            ),
          },
          {
            title: "关联 Skill",
            dataIndex: "skillKeys",
            render: (v: string[]) => <Tags values={v} />,
          },
          { title: "备注", dataIndex: "note", render: (v) => v || "—" },
        ]}
      />
    </Card>
  );
}

function ExecutionList() {
  return (
    <>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        `reservedPower` 是预授权，`settledPower` 是实际结算。失败时必须全额释放预授权。
        当前为空：Skill 执行引擎尚未实现。
      </Typography.Paragraph>
      <DataTable
        path="/api/v1/admin/skills/executions"
        columns={[
          { title: "Skill", dataIndex: "skillKey", width: 180 },
          { title: "分级", dataIndex: "tier", width: 100 },
          { title: "触发", dataIndex: "trigger", width: 140 },
          { title: "形态", dataIndex: "formKey", width: 100, render: (v) => <StatusTag value={v} /> },
          { title: "状态", dataIndex: "status", width: 140, render: (v) => <StatusTag value={v} /> },
          { title: "预授权", dataIndex: "reservedPower", width: 90, align: "right" },
          { title: "结算", dataIndex: "settledPower", width: 80, align: "right" },
          { title: "错误码", dataIndex: "errorCode", width: 130, render: (v) => v || "—" },
          { title: "时间", dataIndex: "createdAt", width: 130, render: (v) => <RelativeTime value={v} /> },
        ]}
      />
    </>
  );
}

function SourceList() {
  return (
    <>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        外部抓取的内容，`trustLevel` 默认 untrusted。当前为空：web.search / web.fetch 尚未实现。
      </Typography.Paragraph>
      <DataTable
        path="/api/v1/admin/skills/external-sources"
        columns={[
          { title: "来源", dataIndex: "provider", width: 140 },
          { title: "URL", dataIndex: "url" },
          { title: "标题", dataIndex: "title", width: 200, render: (v) => v || "—" },
          { title: "可信级别", dataIndex: "trustLevel", width: 120, render: (v) => <StatusTag value={v} /> },
          { title: "抓取时间", dataIndex: "fetchedAt", width: 130, render: (v) => <RelativeTime value={v} /> },
          { title: "过期", dataIndex: "expiresAt", width: 130, render: (v) => <RelativeTime value={v} /> },
        ]}
      />
    </>
  );
}
